import React, { useState } from 'react';
import { api } from '../services/api';
import { useAgentAlias } from '../services/agentAliasContext';

interface ReportData {
  recordings: any[];
  totalCount: number;
  systems: Record<string, number>;
  agents: Record<string, number>;
  transcribed: number;
  totalDuration: number;
  dateRange: { from: string; to: string };
}

function ComplianceReport() {
  const { resolveAgent } = useAgentAlias();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generateReport = async () => {
    if (!dateFrom || !dateTo) {
      setError('Please select a date range');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params: any = { dateFrom, dateTo };
      if (systemFilter) params.systemId = systemFilter;
      const data = await api.search(params);
      const recordings = data.recordings || [];

      const systems: Record<string, number> = {};
      const agents: Record<string, number> = {};
      let transcribed = 0;
      let totalDuration = 0;

      for (const r of recordings) {
        systems[r.systemId || 'unknown'] = (systems[r.systemId || 'unknown'] || 0) + 1;
        if (r.agentId && r.agentId !== 'unknown') agents[r.agentId] = (agents[r.agentId] || 0) + 1;
        if (r.hasTranscript || r.transcript) transcribed++;
        if (r.duration) totalDuration += typeof r.duration === 'string' ? parseInt(r.duration) : r.duration;
      }

      setReport({
        recordings,
        totalCount: recordings.length,
        systems,
        agents,
        transcribed,
        totalDuration,
        dateRange: { from: dateFrom, to: dateTo },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to generate report');
    }
    setLoading(false);
  };

  const handlePrint = () => window.print();

  const handleExportCsv = () => {
    if (!report) return;
    const headers = ['Date', 'System', 'Agent', 'Phone', 'Direction', 'Duration (sec)', 'Filename', 'Transcript', 'Storage Status'];
    const rows = report.recordings.map(r => [
      r.recordDate || '', r.systemId || '', r.agentId || '', r.callerId || '',
      r.direction || '', r.duration || '', r.originalFilename || '',
      r.hasTranscript ? 'Yes' : 'No', r.storageStatus || 'nas_only',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `compliance-report-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };


  return (
    <div>
      <div className="page-header">
        <h1>Compliance Report</h1>
        <p>Generate recording reports for auditors and compliance review</p>
      </div>

      {/* Report parameters */}
      <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Report Parameters</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '4px' }}>From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
          </div>
          <div>
            <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '4px' }}>To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
          </div>
          <div>
            <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '4px' }}>System</label>
            <select value={systemFilter} onChange={e => setSystemFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', background: '#fff' }}>
              <option value="">All Systems</option>
              <option value="pulsedial">PulseDial</option>
              <option value="dialedin">DialedIn</option>
              <option value="vicidial">Vicidial</option>
              <option value="3cx-legacy">3CX Legacy</option>
            </select>
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button type="button" onClick={generateReport} disabled={loading}
              style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: loading ? '#999' : '#00bcd4', color: '#fff', fontSize: '14px', cursor: 'pointer' }}>
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </div>
        {error && <div style={{ marginTop: '8px', color: '#c62828', fontSize: '13px' }}>{error}</div>}
      </div>

      {/* Report output */}
      {report && (
        <div id="compliance-report">
          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }} className="no-print">
            <button type="button" onClick={handlePrint}
              style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', fontSize: '13px', cursor: 'pointer' }}>
              Print Report
            </button>
            <button type="button" onClick={handleExportCsv}
              style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', fontSize: '13px', cursor: 'pointer' }}>
              Export CSV
            </button>
          </div>

          {/* Report header */}
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h2 style={{ margin: '0 0 4px 0' }}>Pulse Storage — Recording Compliance Report</h2>
            <p style={{ color: '#666', margin: '0 0 16px 0' }}>
              {report.dateRange.from} to {report.dateRange.to} {systemFilter && ` — ${systemFilter}`}
            </p>
            <p style={{ color: '#999', fontSize: '12px', margin: 0 }}>
              Generated: {new Date().toLocaleString()} | Woxom Health | recordings.woxomhealth.com
            </p>
          </div>

          {/* Summary stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Recordings</h3>
              <div className="value">{report.totalCount}</div>
            </div>
            <div className="stat-card">
              <h3>Transcribed</h3>
              <div className="value">{report.transcribed}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>{report.totalCount > 0 ? Math.round(report.transcribed / report.totalCount * 100) : 0}%</div>
            </div>
            <div className="stat-card">
              <h3>Total Duration</h3>
              <div className="value" style={{ fontSize: '20px' }}>{formatDuration(report.totalDuration)}</div>
            </div>
            <div className="stat-card">
              <h3>Systems</h3>
              <div className="value">{Object.keys(report.systems).length}</div>
            </div>
          </div>

          {/* Per-system breakdown */}
          <h3>By System</h3>
          <table className="data-table">
            <thead>
              <tr><th>System</th><th>Recordings</th><th>% of Total</th></tr>
            </thead>
            <tbody>
              {Object.entries(report.systems).sort((a, b) => b[1] - a[1]).map(([sys, count]) => (
                <tr key={sys}>
                  <td><span className={`system-tag ${sys === '3cx-legacy' ? 'legacy' : sys}`}>{sys}</span></td>
                  <td>{count}</td>
                  <td>{Math.round(count / report.totalCount * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Per-agent breakdown */}
          {Object.keys(report.agents).length > 0 && (
            <>
              <h3>By Agent</h3>
              <table className="data-table">
                <thead>
                  <tr><th>Agent</th><th>Recordings</th><th>% of Total</th></tr>
                </thead>
                <tbody>
                  {Object.entries(report.agents).sort((a, b) => b[1] - a[1]).map(([agent, count]) => (
                    <tr key={agent}>
                      <td>{resolveAgent(agent)}</td>
                      <td>{count}</td>
                      <td>{Math.round(count / report.totalCount * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Detailed recording list */}
          <h3>All Recordings ({report.totalCount})</h3>
          <table className="data-table" style={{ fontSize: '12px' }}>
            <thead>
              <tr>
                <th>Date</th><th>System</th><th>Agent</th><th>Phone</th><th>Dir</th><th>Duration</th><th>Transcript</th><th>Filename</th>
              </tr>
            </thead>
            <tbody>
              {report.recordings.slice(0, 500).map((r, i) => (
                <tr key={i}>
                  <td>{r.recordDate}</td>
                  <td>{r.systemId}</td>
                  <td>{r.agentId || '-'}</td>
                  <td>{r.callerId || '-'}</td>
                  <td>{r.direction || '-'}</td>
                  <td>{r.duration ? `${Math.floor(r.duration / 60)}:${(r.duration % 60).toString().padStart(2, '0')}` : '-'}</td>
                  <td>{r.hasTranscript ? 'Yes' : 'No'}</td>
                  <td style={{ maxWidth: '200px', wordBreak: 'break-all' }}>{r.originalFilename}</td>
                </tr>
              ))}
              {report.totalCount > 500 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: '#999' }}>Showing first 500 of {report.totalCount}. Use CSV export for full list.</td></tr>
              )}
            </tbody>
          </table>

          {/* Compliance footer */}
          <div style={{ marginTop: '24px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px', color: '#666' }}>
            <strong>Retention Policy:</strong> All recordings retained for 10 years per Medicare compliance requirements.
            Storage: NAS (3 years instant access) + S3 Glacier Deep Archive (7 years archival).
            No recordings can be deleted from the dashboard or API. Deletion requires direct NAS access with explicit authorization.
          </div>
        </div>
      )}
    </div>
  );
}

export default ComplianceReport;
